// Base CSS injected by the runtime: shadcn-named theme variables (D12), tasteful
// prose defaults (anti-slop), the floating toolbar, and the offline banner.
// Variable names mirror shadcn; values are direct colors referenced via var() —
// the current shadcn/Tailwind-4 convention.

export const BASE_CSS = `
:root{
  --background:#ffffff; --foreground:#0a0a0a;
  --card:#ffffff; --card-foreground:#0a0a0a;
  --primary:#4f46e5; --primary-foreground:#ffffff;
  --secondary:#f1f5f9; --secondary-foreground:#0f172a;
  --muted:#f1f5f9; --muted-foreground:#64748b;
  --accent:#eef2ff; --accent-foreground:#312e81;
  --border:#e5e7eb; --ring:#4f46e5;
  --radius:0.5rem;
  --pagelet-sans:ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  --pagelet-mono:ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace;
}
[data-theme="dark"]{
  --background:#0a0a0a; --foreground:#fafafa;
  --card:#111113; --card-foreground:#fafafa;
  --primary:#818cf8; --primary-foreground:#0a0a0a;
  --secondary:#1e293b; --secondary-foreground:#f1f5f9;
  --muted:#18181b; --muted-foreground:#a1a1aa;
  --accent:#1e1b4b; --accent-foreground:#e0e7ff;
  --border:#27272a; --ring:#818cf8;
}
/* Native widgets (scrollbars, form controls) must follow the resolved mode, not the OS. */
:root{color-scheme:light}
[data-theme="dark"]{color-scheme:dark}
body{
  margin:0; background:var(--background); color:var(--foreground);
  font-family:var(--font-sans, var(--pagelet-sans));
  -webkit-font-smoothing:antialiased; text-rendering:optimizeLegibility;
}
.pagelet-prose{
  max-width:46rem; margin:0 auto; padding:2.5rem 1.25rem 5rem;
  line-height:1.7; font-size:1.0625rem;
}
.pagelet-prose h1,.pagelet-prose h2,.pagelet-prose h3{
  line-height:1.25; font-weight:650; letter-spacing:-0.01em; margin:2em 0 0.6em;
}
.pagelet-prose h1{font-size:2rem; margin-top:0}
.pagelet-prose h2{font-size:1.5rem; padding-bottom:.3em; border-bottom:1px solid var(--border)}
.pagelet-prose h3{font-size:1.2rem}
.pagelet-prose p,.pagelet-prose ul,.pagelet-prose ol{margin:0 0 1.1em}
.pagelet-prose a{color:var(--primary); text-underline-offset:2px}
.pagelet-prose code{
  font-family:var(--pagelet-mono); font-size:.875em;
  background:var(--muted); padding:.15em .4em; border-radius:calc(var(--radius) - 2px);
}
.pagelet-prose pre{
  background:var(--muted); color:var(--foreground);
  padding:1rem 1.1rem; border-radius:var(--radius); overflow:auto;
  border:1px solid var(--border); margin:0 0 1.2em;
}
.pagelet-prose pre code{background:none; padding:0}
.pagelet-prose blockquote{
  margin:0 0 1.2em; padding:.2em 1rem; border-left:3px solid var(--border);
  color:var(--muted-foreground);
}
.pagelet-prose table{border-collapse:collapse; width:100%; margin:0 0 1.2em; font-size:.95em}
.pagelet-prose th,.pagelet-prose td{border:1px solid var(--border); padding:.5em .75em; text-align:left}
.pagelet-prose th{background:var(--muted)}
.pagelet-prose img{max-width:100%; border-radius:var(--radius)}
.pagelet-prose hr{border:none; border-top:1px solid var(--border); margin:2em 0}

#pagelet-toolbar{
  position:fixed; bottom:1rem; right:1rem; z-index:9999;
  display:flex; gap:.2rem; padding:.3rem;
  background:color-mix(in srgb, var(--card) 72%, transparent);
  -webkit-backdrop-filter:blur(14px) saturate(1.5); backdrop-filter:blur(14px) saturate(1.5);
  border:1px solid color-mix(in srgb, var(--border) 70%, transparent);
  border-radius:calc(var(--radius) + 6px);
  box-shadow:0 8px 32px rgba(0,0,0,.14), inset 0 1px 0 color-mix(in srgb, var(--foreground) 6%, transparent);
  cursor:grab; touch-action:none; user-select:none; -webkit-user-select:none;
  transform-origin:bottom right;
}
#pagelet-toolbar.pagelet-tb-dragging{cursor:grabbing; box-shadow:0 16px 48px rgba(0,0,0,.22)}
#pagelet-toolbar .pagelet-tb-hide{
  position:absolute; top:-.55rem; left:-.55rem; z-index:2;
  width:1.15rem; height:1.15rem; padding:0;
  display:flex; align-items:center; justify-content:center;
  background:var(--card); border:1px solid var(--border); border-radius:9999px;
  color:var(--muted-foreground); box-shadow:0 2px 8px rgba(0,0,0,.14);
  opacity:0; transform:scale(.4); pointer-events:none;
  transition:opacity .18s ease, transform .18s cubic-bezier(.34,1.56,.64,1);
}
#pagelet-toolbar .pagelet-tb-hide svg{width:.65rem; height:.65rem}
#pagelet-toolbar:hover .pagelet-tb-hide,
#pagelet-toolbar:focus-within .pagelet-tb-hide{opacity:1; transform:scale(1); pointer-events:auto}
#pagelet-toolbar .pagelet-tb-hide:hover{background:var(--accent); color:var(--accent-foreground)}

/* Collapsed state: a small dot that grows on hover and restores the toolbar on click. */
#pagelet-toolbar-dot{
  all:unset; box-sizing:border-box;
  position:fixed; bottom:1.25rem; right:1.25rem; z-index:9999;
  width:.8rem; height:.8rem; border-radius:9999px; cursor:pointer;
  background:color-mix(in srgb, var(--foreground) 55%, transparent);
  box-shadow:0 2px 10px rgba(0,0,0,.25);
  transition:transform .2s cubic-bezier(.34,1.56,.64,1), background .2s ease, box-shadow .2s ease;
}
#pagelet-toolbar-dot span{
  position:absolute; inset:-.35rem; border-radius:9999px;
  border:1px solid color-mix(in srgb, var(--foreground) 30%, transparent);
  opacity:0; transform:scale(.6);
  transition:opacity .2s ease, transform .2s cubic-bezier(.34,1.56,.64,1);
}
#pagelet-toolbar-dot:hover,#pagelet-toolbar-dot:focus-visible{
  transform:scale(1.75); background:var(--foreground);
  box-shadow:0 4px 16px rgba(0,0,0,.3);
}
#pagelet-toolbar-dot:hover span,#pagelet-toolbar-dot:focus-visible span{opacity:1; transform:scale(1)}
#pagelet-toolbar-dot:focus-visible{outline:2px solid var(--ring); outline-offset:2px}
#pagelet-toolbar button{
  all:unset; box-sizing:border-box; cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  width:2rem; height:2rem; border-radius:var(--radius); color:var(--muted-foreground);
  transition:background .12s ease, color .12s ease;
}
#pagelet-toolbar button svg{width:1.05rem; height:1.05rem; display:block}
#pagelet-toolbar button:hover{background:var(--accent); color:var(--accent-foreground)}
#pagelet-toolbar button:focus-visible{outline:2px solid var(--ring); outline-offset:1px}
#pagelet-toolbar button.pagelet-copied{color:var(--primary)}

#pagelet-update-toast button svg{width:.85rem; height:.85rem; display:block}
#pagelet-update-toast [data-act="dismiss"]{display:flex; align-items:center; justify-content:center}

#pagelet-offline{
  max-width:46rem; margin:1.5rem auto; padding:.85rem 1.1rem;
  font-family:var(--font-sans, var(--pagelet-sans)); font-size:.95rem;
  color:var(--muted-foreground); background:var(--muted);
  border:1px solid var(--border); border-radius:var(--radius);
}

/* Version awareness: the refresh toast, the toolbar's timeline panel, and the pinned banner. */
#pagelet-update-toast{
  position:fixed; bottom:4.25rem; right:1rem; z-index:9999;
  display:flex; align-items:center; gap:.6rem; padding:.55rem .5rem .55rem .9rem;
  background:color-mix(in srgb, var(--card) 82%, transparent);
  -webkit-backdrop-filter:blur(14px) saturate(1.5); backdrop-filter:blur(14px) saturate(1.5);
  border:1px solid color-mix(in srgb, var(--border) 70%, transparent);
  border-radius:calc(var(--radius) + 6px);
  box-shadow:0 8px 32px rgba(0,0,0,.14);
  font-family:var(--font-sans, var(--pagelet-sans)); font-size:.9rem; color:var(--foreground);
}
#pagelet-update-toast button{all:unset; cursor:pointer; padding:.25rem .6rem; border-radius:var(--radius)}
#pagelet-update-toast [data-act="refresh"]{background:var(--primary); color:var(--primary-foreground); font-weight:600}
#pagelet-update-toast [data-act="dismiss"]{color:var(--muted-foreground); padding:.25rem .4rem}
#pagelet-update-toast [data-act="dismiss"]:hover{color:var(--foreground)}

#pagelet-versions{
  position:fixed; bottom:4.25rem; right:1rem; z-index:9999;
  width:16rem; max-height:60vh; overflow:auto; padding:.4rem;
  background:color-mix(in srgb, var(--card) 86%, transparent);
  -webkit-backdrop-filter:blur(14px) saturate(1.5); backdrop-filter:blur(14px) saturate(1.5);
  border:1px solid color-mix(in srgb, var(--border) 70%, transparent);
  border-radius:calc(var(--radius) + 6px);
  box-shadow:0 8px 32px rgba(0,0,0,.14);
  font-family:var(--font-sans, var(--pagelet-sans)); font-size:.85rem;
}
#pagelet-versions .pagelet-v-title{
  padding:.35rem .55rem .45rem; font-weight:600; color:var(--muted-foreground);
  font-size:.72rem; text-transform:uppercase; letter-spacing:.04em;
}
#pagelet-versions button{all:unset; box-sizing:border-box; cursor:pointer; display:block; width:100%; padding:.4rem .55rem; border-radius:var(--radius)}
#pagelet-versions button:hover{background:var(--accent)}
#pagelet-versions button.current{background:var(--muted)}
#pagelet-versions .pagelet-v-label{display:block; color:var(--foreground); font-weight:550}
#pagelet-versions .pagelet-v-meta{display:block; color:var(--muted-foreground); font-size:.85em}

#pagelet-version-banner{
  position:fixed; top:0; left:50%; transform:translateX(-50%); z-index:9999;
  display:flex; gap:.75rem; align-items:center; padding:.45rem 1rem; max-width:92vw;
  background:var(--accent); color:var(--accent-foreground);
  border:1px solid var(--border); border-top:none; border-radius:0 0 var(--radius) var(--radius);
  font-family:var(--font-sans, var(--pagelet-sans)); font-size:.85rem; white-space:nowrap;
}
#pagelet-version-banner a{color:inherit; font-weight:600}

#pagelet-progress{
  position:fixed; top:0; left:0; height:3px; width:0;
  background:var(--primary); z-index:9998; transition:width .1s linear;
}
/* Floating TOC: top-aligned, hugging the left viewport edge (with a little breathing
   room), never under top bars. Scroll-spy highlights the current section; clicks
   smooth-scroll (gated on reduced-motion). */
#pagelet-toc{
  position:fixed; top:clamp(4.5rem, 14vh, 8rem); left:clamp(1rem, 3.5vw, 3rem);
  width:11rem; max-height:min(30rem, 64vh); overflow:auto;
  padding:.25rem 0; font-size:.8rem; display:none; z-index:40;
  scrollbar-width:none;
}
#pagelet-toc::-webkit-scrollbar{display:none}
#pagelet-toc ul{list-style:none; margin:0; padding:0; border-left:1px solid var(--border)}
#pagelet-toc li{margin:0}
#pagelet-toc li.lvl-h3 a{padding-left:1.6rem; font-size:.95em}
#pagelet-toc a{
  display:block; padding:.3rem .9rem; color:var(--muted-foreground);
  text-decoration:none; border-left:2px solid transparent; margin-left:-1px;
  line-height:1.35; transition:color .15s ease, border-color .15s ease;
}
#pagelet-toc a:hover{color:var(--foreground)}
#pagelet-toc a.active{color:var(--foreground); border-left-color:var(--foreground); font-weight:550}
/* Collapsible TOC: hover-revealed hide button; the hidden state slides the rail out left. */
#pagelet-toc{
  transition:transform .28s cubic-bezier(.16,1,.3,1), opacity .22s ease, visibility .28s;
}
#pagelet-toc.pagelet-toc-hidden{
  transform:translateX(-14px); opacity:0; visibility:hidden; pointer-events:none;
}
#pagelet-toc-enter{animation:pagelet-toc-in .3s cubic-bezier(.16,1,.3,1)}
@keyframes pagelet-toc-in{
  from{transform:translateX(-14px); opacity:0}
  to{transform:translateX(0); opacity:1}
}
#pagelet-toc .pagelet-toc-hide{
  all:unset; box-sizing:border-box; cursor:pointer;
  position:absolute; top:-.15rem; right:-1.1rem; z-index:2;
  display:flex; align-items:center; justify-content:center;
  width:1.4rem; height:1.4rem; border-radius:9999px;
  color:var(--muted-foreground); background:var(--card); border:1px solid var(--border);
  box-shadow:0 2px 8px rgba(0,0,0,.12);
  opacity:0; transform:translateX(4px) scale(.6); pointer-events:none;
  transition:opacity .18s ease, transform .18s cubic-bezier(.34,1.56,.64,1), color .15s ease;
}
#pagelet-toc .pagelet-toc-hide svg{width:.8rem; height:.8rem}
#pagelet-toc:hover .pagelet-toc-hide,
#pagelet-toc:focus-within .pagelet-toc-hide{opacity:1; transform:translateX(0) scale(1); pointer-events:auto}
#pagelet-toc .pagelet-toc-hide:hover{color:var(--foreground)}

/* Collapsed state: a slim tab docked to the left edge; grows to reveal its label on hover. */
#pagelet-toc-tab{
  all:unset; box-sizing:border-box; cursor:pointer;
  position:fixed; left:0; top:50%; transform:translateY(-50%); z-index:41;
  display:flex; align-items:center; gap:.45rem;
  padding:.55rem .55rem .55rem .45rem;
  color:var(--muted-foreground); background:color-mix(in srgb, var(--card) 80%, transparent);
  -webkit-backdrop-filter:blur(10px); backdrop-filter:blur(10px);
  border:1px solid color-mix(in srgb, var(--border) 80%, transparent); border-left:none;
  border-radius:0 var(--radius) var(--radius) 0;
  box-shadow:0 4px 16px rgba(0,0,0,.12);
  animation:pagelet-toc-tab-in .3s cubic-bezier(.34,1.56,.64,1);
  transition:color .18s ease, padding .25s cubic-bezier(.34,1.56,.64,1), box-shadow .2s ease, transform .25s cubic-bezier(.34,1.56,.64,1);
}
@keyframes pagelet-toc-tab-in{
  from{transform:translate(-60%,-50%); opacity:0}
  to{transform:translate(0,-50%); opacity:1}
}
#pagelet-toc-tab svg{width:.95rem; height:.95rem; flex:none}
#pagelet-toc-tab .pagelet-toc-tab-label{
  max-width:0; overflow:hidden; white-space:nowrap;
  font-family:var(--font-sans, var(--pagelet-sans)); font-size:.78rem; font-weight:550;
  opacity:0; transition:max-width .25s cubic-bezier(.16,1,.3,1), opacity .2s ease;
}
#pagelet-toc-tab:hover,#pagelet-toc-tab:focus-visible{
  color:var(--foreground); padding-right:.8rem; padding-left:.6rem;
  transform:translate(2px,-50%);
  box-shadow:0 8px 24px rgba(0,0,0,.16);
}
#pagelet-toc-tab:hover .pagelet-toc-tab-label,
#pagelet-toc-tab:focus-visible .pagelet-toc-tab-label{max-width:4rem; opacity:1}
#pagelet-toc-tab:focus-visible{outline:2px solid var(--ring); outline-offset:1px}
@media print{#pagelet-toc-tab{display:none !important}}
@media (prefers-reduced-motion: reduce){
  #pagelet-toc,#pagelet-toc .pagelet-toc-hide,#pagelet-toc-tab,#pagelet-toc-tab .pagelet-toc-tab-label{transition:none}
  #pagelet-toc-enter,#pagelet-toc-tab{animation:none}
}
/* Anchor jumps must clear sticky page headers. */
.pagelet-prose :is(h1,h2,h3,h4){scroll-margin-top:4.5rem}
@media (prefers-reduced-motion: no-preference){
  html{scroll-behavior:smooth}
}
@media (min-width:1180px){ #pagelet-toc{display:block} }

.pagelet-chart{max-width:46rem; margin:1.5rem auto; padding:0 1.25rem}

/* Diagrams break out a little wider than the prose column for readability; the
   zoom modal carries real pan/zoom/fullscreen. */
.pagelet-mermaid{
  position:relative; box-sizing:border-box; cursor:zoom-in;
  width:min(60rem,92vw); margin:1.75rem 0 1.75rem 50%; transform:translateX(-50%);
  padding:.75rem; border:1px solid var(--border); border-radius:var(--radius); background:var(--card);
}
.pagelet-mermaid svg{display:block; margin:0 auto; max-width:100%; height:auto; max-height:62vh}
.pagelet-mermaid-expand{
  all:unset; box-sizing:border-box;
  position:absolute; top:.5rem; right:.5rem; z-index:2;
  cursor:pointer; width:1.9rem; height:1.9rem; line-height:1.75rem; text-align:center;
  color:var(--muted-foreground); background:var(--card); border:1px solid var(--border);
  border-radius:calc(var(--radius) - 2px); box-shadow:0 1px 3px rgba(0,0,0,.12);
}
.pagelet-mermaid-expand:hover{color:var(--foreground); background:var(--accent)}

/* In-viewport zoom modal */
.pagelet-mmd-overlay{
  position:fixed; inset:0; z-index:10000; display:flex; align-items:center; justify-content:center;
  padding:max(2.5vmin,1rem); background:rgba(0,0,0,.5);
  background:color-mix(in srgb, var(--background) 72%, transparent);
  -webkit-backdrop-filter:blur(3px); backdrop-filter:blur(3px);
}
.pagelet-mmd-dialog{
  position:relative; display:flex; flex-direction:column; overflow:hidden;
  width:min(96vw,1400px); height:min(92vh,1000px);
  background:var(--card); border:1px solid var(--border);
  border-radius:calc(var(--radius) + 6px); box-shadow:0 24px 64px rgba(0,0,0,.4);
}
.pagelet-mmd-bar{
  display:flex; align-items:center; gap:.2rem; padding:.4rem .5rem;
  border-bottom:1px solid var(--border); background:var(--card); color:var(--foreground);
}
.pagelet-mmd-bar .pagelet-mmd-title{
  font-size:.85rem; font-weight:600; padding-left:.35rem; color:var(--muted-foreground);
}
.pagelet-mmd-bar .spacer{flex:1}
.pagelet-mmd-bar button{
  all:unset; cursor:pointer; min-width:2rem; height:2rem; padding:0 .45rem; line-height:2rem;
  text-align:center; border-radius:var(--radius); color:var(--foreground); font-size:1.05rem;
}
.pagelet-mmd-bar button:hover{background:var(--accent); color:var(--accent-foreground)}
.pagelet-mmd-stage{flex:1; min-height:0; overflow:hidden; background:var(--background); touch-action:none}
.pagelet-mmd-stage svg{display:block; width:100%; height:100%; max-height:none}
.pagelet-mmd-overlay:fullscreen{padding:0}
.pagelet-mmd-overlay:fullscreen .pagelet-mmd-dialog{
  width:100vw; height:100vh; max-width:none; border:none; border-radius:0; box-shadow:none;
}

/* Mermaid theming hardening — track the page theme + guarantee contrast on the
   elements mermaid's themeVariables tend to under-style (gantt grid/labels). */
svg.pagelet-diagram text{font-family:var(--pagelet-sans)}
svg.pagelet-diagram .grid .tick line{stroke:var(--border) !important; opacity:.4}
svg.pagelet-diagram .grid path{stroke:var(--border) !important}
svg.pagelet-diagram .grid .tick text{fill:var(--muted-foreground) !important}
svg.pagelet-diagram .titleText{fill:var(--foreground) !important}
svg.pagelet-diagram text[class*="sectionTitle"]{fill:var(--foreground) !important}
svg.pagelet-diagram [class*="taskTextOutside"]{fill:var(--foreground) !important}
svg.pagelet-diagram .today{stroke:var(--primary) !important}

@media print{
  #pagelet-toolbar,#pagelet-toolbar-dot,#pagelet-progress,#pagelet-toc,#pagelet-update-toast,#pagelet-versions,#pagelet-version-banner,.pagelet-copy,.pagelet-mermaid-expand,.pagelet-mmd-overlay{display:none !important}
  .pagelet-prose{max-width:none}
  .pagelet-mermaid{width:auto; margin:1.5rem 0; transform:none}
}
`
